import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Label } from '@/components/shadcn/label'
import {
  getGetApiCompaniesCurrentActionmodulesQueryKey,
  usePostApiCompaniesCurrentActionmodulesActionModuleIdCopy,
} from '@/api/generated/company-management-user/company-management-user'
import type { CopyActionModuleParameters } from '@/api/model'
import { notify } from '@/lib/notify'
import type { ActionModule } from '../types'

/**
 * The parts of a module the user can carry over. Keys are the request flags, the labels
 * reuse the wizard tab names (same slices of the module).
 */
const COPY_OPTIONS = [
  { key: 'copySubtypes', labelKey: 'tabs.subtypes' },
  { key: 'copySchedulingStrategy', labelKey: 'tabs.scheduling' },
  { key: 'copyRewardCalculationType', labelKey: 'tabs.reward' },
  { key: 'copyIncentivationSource', labelKey: 'tabs.incentivationSource' },
  { key: 'copyAssignment', labelKey: 'tabs.assignment' },
] as const satisfies ReadonlyArray<{ key: keyof CopyActionModuleParameters; labelKey: string }>

const ALL_SELECTED: Required<CopyActionModuleParameters> = {
  copySubtypes: true,
  copySchedulingStrategy: true,
  copyRewardCalculationType: true,
  copyIncentivationSource: true,
  copyAssignment: true,
}

export function CopyDialog({
  actionModuleId,
  onClose,
  onCopied,
}: {
  actionModuleId: number
  onClose: () => void
  /** Called with the created copy after a successful duplicate. */
  onCopied?: (copy: ActionModule) => void
}) {
  const { t } = useTranslation(['action-modules', 'action-modules-wizard', 'common'])
  const queryClient = useQueryClient()
  const [selection, setSelection] = useState<Required<CopyActionModuleParameters>>(ALL_SELECTED)

  const copy = usePostApiCompaniesCurrentActionmodulesActionModuleIdCopy()

  async function handleCopy() {
    try {
      const response = await copy.mutateAsync({ actionModuleId, data: selection })
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentActionmodulesQueryKey() })
      notify.success(t('copyDialog.success'))
      onCopied?.(response.data)
      onClose()
    } catch (error) {
      notify.apiError(error, t('copyDialog.error'))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('copyDialog.title')}</DialogTitle>
          <DialogDescription>{t('copyDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Label>{t('copyDialog.contents')}</Label>
          <div className="flex flex-col gap-2.5">
            {COPY_OPTIONS.map(({ key, labelKey }) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  id={`copy-${key}`}
                  checked={selection[key]}
                  onCheckedChange={v => setSelection(prev => ({ ...prev, [key]: !!v }))}
                />
                <label htmlFor={`copy-${key}`} className="text-sm cursor-pointer">
                  {t(labelKey, { ns: 'action-modules-wizard' })}
                </label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={copy.isPending}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleCopy} disabled={copy.isPending}>
            {t('buttons.apply', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
